import React, { useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { useOutsideAlerter } from 'Services/helpers';
import CssHelper from 'Services/Utils/Css/CssHelper';
import styles from './Dropdown.module.sass';

const DropdownContext = React.createContext<any>({});

export function useDropdown() {
    const context = useContext(DropdownContext)
    if (context === undefined) {
        throw new Error('useCount must be used within a CountProvider')
    }

    return context;
}

export function DropdownConsumer({children}) {
    return (
        <DropdownContext.Consumer>
            {context => {
                if (context === undefined) {
                    throw new Error('CountConsumer must be used within a CountProvider')
                }
                return children(context)
            }}
        </DropdownContext.Consumer>
    )
}

const Dropdown = ({
    children,

    disableCloseOnOutsideClick,
    disableCloseOnClick,
    triggerAction = 'click',
    trigger,

    position
}: Props) => {
    const [state, setState] = useState({
        isOpen: false
    });
    const wrapperRef = useRef(null);

    const [popperRef, setPopperRef] = useState<any>(null);
    const { styles: popperStyle, attributes } = usePopper(
        wrapperRef?.current,
        popperRef,
        {
            placement: 'bottom-start',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 10]
                    }
                }
            ]
        }
    );

    useOutsideAlerter(() => {
        setState((state) => ({
            ...state,
            isOpen: false
        }));
    }, [wrapperRef],
        !disableCloseOnOutsideClick
        && triggerAction === 'click'
        && state.isOpen
    );

    const handleClick = () => {
        if (triggerAction !== 'click') return;
        setState((state) => ({
            ...state,
            isOpen: !state.isOpen
        }));
    };

    const handleMouseEnter = () => {
        if (triggerAction !== 'hover') return;
        setState((state) => ({
            ...state,
            isOpen: true
        }));
    }

    const handleMouseLeave = () => {
        if (triggerAction !== 'hover') return;
        setState((state) => ({
            ...state,
            isOpen: false
        }));
    }

    const closeDropdown = () => setState((state) => ({
        ...state,
        isOpen: false
    }));

    const closeDropdownOnContent = () => {
        if (disableCloseOnClick) return;
        closeDropdown()
    }

    return <DropdownContext.Provider
        value={{
            closeDropdown
        }}
    >
        <div
            ref={wrapperRef}
            className={styles['dropdown']}
        >
            {
                React.cloneElement(trigger, {
                    onClick: handleClick,
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave: handleMouseLeave,
                    style: {
                        ...trigger?.props?.style,
                        cursor: triggerAction === 'click' ? 'pointer' : 'default'
                    }
                })
            }
            {
                state.isOpen &&
                children &&
                ReactDOM.createPortal(
                    <div
                        data-dropdown='true'
                        ref={setPopperRef}
                        className={CssHelper.createCssClass(`
                            ${styles['dropdown__content']}
                            ${styles['dropdown__content__position--'+(position||'left')]}
                        `)}
                        style={popperStyle.popper}
                        onClick={closeDropdownOnContent}
                        {...attributes.popper}
                    >
                        { children }
                    </div>,
                    // @ts-ignore
                    document.getElementById('root')
                )
            }
        </div>
    </DropdownContext.Provider>;
}

interface Props {
    children: any

    triggerAction?: 'click' | 'hover'
    trigger: any

    disableCloseOnClick?: boolean

    disableCloseOnOutsideClick?: boolean
    closeDropdown?: any
    position?: 'left' | 'right'
}

export default Dropdown;